/**
 * Modal de SUBIDA DE RANGO (screen #28 `modal_de_subida_de_rango`): celebración
 * al ascender, con las nuevas recompensas y las CTA de reclamar/compartir.
 * Reutilizable: se dispara en el level-up real (Wave 2 lo cablea al cruzar un
 * umbral) y desde "Reclamar recompensas" en la pantalla de rangos.
 *
 * Sobre Kobalte `Dialog` (accesible, back-dismiss, foco atrapado). La animación
 * del emblema usa `motion-safe:` → respeta `prefers-reduced-motion`.
 */
import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { PartyPopper, Share2 } from "lucide-solid";
import type { RankDef } from "@famidex/shared";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/providers/i18n-provider";
import { rankIcon, rankName, rewardIcon, rewardLabel } from "../rank-presentation";

interface RankUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rank: RankDef | null;
  /** Reclamar recompensas (marca como reclamadas). */
  onClaim?: () => void;
  /** Compartir logro (share sheet — lo cablea D/social; opcional). */
  onShare?: () => void;
  /** Copia del encabezado: level-up ("¡Felicidades!") vs. reclamar. */
  mode?: "levelup" | "claim";
}

export function RankUpModal(props: RankUpModalProps) {
  const { t } = useI18n();

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      contentClass="max-w-lg p-0 overflow-hidden"
      role="dialog"
    >
      <Show when={props.rank}>
        {(rank) => {
          const Icon = rankIcon(rank().icon);
          return (
            <div
              class="flex flex-col items-center px-6 pb-6 pt-10 text-center"
              data-testid="rank-up-modal"
            >
              {/* Emblema del rango */}
              <div class="relative mb-5 grid h-32 w-32 place-items-center rounded-full border-4 border-warning bg-secondary shadow-lg motion-safe:animate-pulse">
                <Icon class="h-16 w-16 text-primary" />
                <span class="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-warning px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-foreground">
                  {rankName(t, rank().id)}
                </span>
              </div>

              <DialogTitle
                class={
                  props.mode === "claim"
                    ? "text-2xl font-bold text-foreground"
                    : "text-2xl font-bold text-danger"
                }
              >
                <Show when={props.mode !== "claim"} fallback={t(msg`Tus recompensas`)}>
                  {t(msg`¡Felicidades!`)}
                </Show>
              </DialogTitle>
              <DialogDescription class="mt-1 text-sm text-muted-foreground">
                <Show
                  when={props.mode !== "claim"}
                  fallback={t(msg`Recompensas de tu rango actual.`)}
                >
                  {t(msg`Has ascendido a`)}{" "}
                  <span class="font-semibold text-primary">{rankName(t, rank().id)}</span>
                </Show>
              </DialogDescription>

              {/* Recompensas nuevas */}
              <Show
                when={rank().rewards.length > 0}
                fallback={
                  <p class="mt-6 text-sm text-muted-foreground">
                    {t(msg`Sigue coleccionando para desbloquear recompensas.`)}
                  </p>
                }
              >
                <div class="mt-6 w-full rounded-xl border border-border/60 bg-card/60 p-5">
                  <p class="mb-4 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Nuevos beneficios`)}
                  </p>
                  <div class="flex flex-wrap justify-center gap-4">
                    <For each={rank().rewards}>
                      {(reward) => {
                        const RewardIcon = rewardIcon(reward.icon);
                        return (
                          <div class="flex w-28 flex-col items-center gap-2 text-center">
                            <span class="grid h-12 w-12 place-items-center rounded-full bg-accent text-accent-foreground">
                              <RewardIcon class="h-6 w-6" />
                            </span>
                            <span class="text-xs leading-tight text-foreground">
                              {rewardLabel(t, reward.key)}
                            </span>
                          </div>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </Show>

              {/* CTA */}
              <div class="mt-7 w-full space-y-3">
                <button
                  type="button"
                  data-testid="rank-up-claim"
                  class="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-semibold text-primary-foreground transition-transform active:scale-[0.98]"
                  onClick={() => {
                    props.onClaim?.();
                    props.onOpenChange(false);
                  }}
                >
                  <span>{t(msg`Reclamar recompensas`)}</span>
                  <PartyPopper class="h-5 w-5" />
                </button>
                <Show when={props.onShare}>
                  <button
                    type="button"
                    class="flex w-full items-center justify-center gap-2 py-2 text-sm text-muted-foreground transition-colors hover:text-primary"
                    onClick={() => props.onShare?.()}
                  >
                    <Share2 class="h-4 w-4" />
                    {t(msg`Compartir logro`)}
                  </button>
                </Show>
              </div>
            </div>
          );
        }}
      </Show>
    </Dialog>
  );
}
